/**
 * Dashboard Role Configuration
 *
 * Defines each user role's dashboard capabilities, default widget layout, and data scope.
 * Role is detected from JWT fields: groupName (group assignment) + title (job title).
 *
 * Role hierarchy (highest → lowest privilege):
 *   super_admin → compliance_owner → leader → manager → supervisor → user
 */

export type DashboardRole =
  | 'super_admin'
  | 'compliance_owner'
  | 'leader'
  | 'manager'
  | 'supervisor'
  | 'user';

export interface RoleConfig {
  label: string;
  description: string;
  color: string;
  bgColor: string;
  icon: string;
  /** Default widget order for first-time load (role-specific). */
  defaultOrder: string[];
  /** Default col-widths (Bootstrap 12-col grid) for each widget. */
  defaultWidths: Record<string, number>;
  /** Widget IDs hidden by default for this role. */
  defaultHidden: string[];
  /** Whether the user can open the Customize panel. */
  canCustomize: boolean;
  /** Whether a compliance-standard filter is shown (CISO / super_admin). */
  showComplianceFilter: boolean;
  /** Whether the team workload chart is shown by default. */
  showTeamView: boolean;
  /** Human-readable scope description shown in the role badge. */
  scopeNote: string;
}

export const ROLE_CONFIGS: Record<DashboardRole, RoleConfig> = {
  super_admin: {
    label: 'Super Admin',
    description: 'Full platform visibility across all tenants and standards',
    scopeNote: 'Viewing: all tenants · all standards · all users',
    color: '#5a9b82',
    bgColor: '#F0FDF4',
    icon: '🛡️',
    defaultOrder: [
      'kpi-cards', 'remediation-kpis', 'unassigned-work', 'remediation-priority', 'remediation-attention',
      'standards-bar', 'workload',
      'status-donut', 'compliance-gauge', 'completion-gauge', 'quick-summary',
    ],
    defaultWidths: {
      'kpi-cards': 12, 'standards-bar': 12, 'workload': 12, 'unassigned-work': 12,
      'remediation-kpis': 12, 'remediation-priority': 5, 'remediation-attention': 7,
      'status-donut': 6, 'compliance-gauge': 3, 'completion-gauge': 3, 'quick-summary': 6,
    },
    defaultHidden: ['my-tasks', 'my-function-surveys'],
    canCustomize: true,
    showComplianceFilter: true,
    showTeamView: true,
  },

  compliance_owner: {
    label: 'Compliance Owner',
    description: 'Compliance-focused view — assigned standards, controls, and risk metrics',
    scopeNote: 'Viewing: assigned compliance frameworks · all controls',
    color: '#3B82F6',
    bgColor: '#EFF6FF',
    icon: '📋',
    defaultOrder: [
      'kpi-cards', 'remediation-kpis', 'standards-bar', 'compliance-gauge', 'completion-gauge',
      'remediation-priority', 'remediation-attention',
      'status-donut', 'quick-summary',
    ],
    defaultWidths: {
      'kpi-cards': 12, 'standards-bar': 12,
      'compliance-gauge': 6, 'completion-gauge': 6,
      'remediation-kpis': 12, 'remediation-priority': 5, 'remediation-attention': 7,
      'status-donut': 8, 'quick-summary': 4,
    },
    defaultHidden: ['workload', 'my-tasks', 'my-function-surveys'],
    canCustomize: true,
    showComplianceFilter: true,
    showTeamView: false,
  },

  // Compliance leaders (group `leader_`) — the backend returns all data for them, so this is
  // a full team/overview layout like super_admin, including the team view and unassigned work.
  leader: {
    label: 'Leader',
    description: 'Full compliance oversight — every team, standard, and unassigned item',
    scopeNote: 'Viewing: all teams · all standards · all users',
    color: '#0E7490',
    bgColor: '#ECFEFF',
    icon: '🧭',
    defaultOrder: [
      'kpi-cards', 'remediation-kpis', 'unassigned-work', 'remediation-priority', 'remediation-attention',
      'standards-bar', 'workload',
      'status-donut', 'compliance-gauge', 'completion-gauge', 'quick-summary',
    ],
    defaultWidths: {
      'kpi-cards': 12, 'standards-bar': 12, 'workload': 12, 'unassigned-work': 12,
      'remediation-kpis': 12, 'remediation-priority': 5, 'remediation-attention': 7,
      'status-donut': 6, 'compliance-gauge': 3, 'completion-gauge': 3, 'quick-summary': 6,
    },
    defaultHidden: ['my-tasks', 'my-function-surveys'],
    canCustomize: true,
    showComplianceFilter: true,
    showTeamView: true,
  },

  manager: {
    label: 'Manager',
    description: 'Team oversight — subordinate workload, team compliance, and performance KPIs',
    scopeNote: 'Viewing: your team\'s tasks and compliance progress',
    color: '#8B5CF6',
    bgColor: '#F5F3FF',
    icon: '👔',
    defaultOrder: [
      'kpi-cards', 'remediation-kpis', 'workload', 'standards-bar',
      'remediation-priority', 'remediation-attention',
      'status-donut', 'compliance-gauge', 'completion-gauge', 'quick-summary',
    ],
    defaultWidths: {
      'kpi-cards': 12, 'workload': 12,
      'remediation-kpis': 12, 'remediation-priority': 5, 'remediation-attention': 7,
      'standards-bar': 7, 'status-donut': 5,
    },
    defaultHidden: ['my-tasks', 'my-function-surveys'],
    canCustomize: true,
    // Now that the backend scopes /compliance-control-actions/report by tenant and Function
    // membership (CN-UAT-06), showing this filter is safe for any team-level role — a
    // non-admin only ever sees their own Functions' data regardless of what they select here.
    showComplianceFilter: true,
    showTeamView: true,
  },

  supervisor: {
    label: 'Supervisor',
    description: 'Department oversight — your team\'s tasks and compliance status',
    scopeNote: 'Viewing: your department\'s tasks and compliance',
    color: '#F59E0B',
    bgColor: '#FFFBEB',
    icon: '👁️',
    defaultOrder: [
      'kpi-cards', 'remediation-kpis', 'workload', 'unassigned-work', 'status-donut',
      'remediation-priority', 'remediation-attention',
      'standards-bar', 'compliance-gauge', 'completion-gauge', 'quick-summary',
    ],
    defaultWidths: {
      'kpi-cards': 12, 'workload': 12, 'unassigned-work': 12,
      'remediation-kpis': 12, 'remediation-priority': 5, 'remediation-attention': 7,
    },
    defaultHidden: ['my-tasks', 'my-function-surveys'],
    canCustomize: true,
    showComplianceFilter: true,
    showTeamView: true,
  },

  user: {
    label: 'Team Member',
    description: 'Personal view — your assigned tasks, deadlines, and personal progress',
    scopeNote: 'Viewing: your tasks only',
    color: '#6B7280',
    bgColor: '#F9FAFB',
    icon: '👤',
    defaultOrder: [
      'kpi-cards', 'my-tasks', 'remediation-kpis', 'remediation-attention',
      'my-function-surveys', 'remediation-priority',
      'status-donut', 'compliance-gauge', 'completion-gauge', 'quick-summary',
    ],
    defaultWidths: {
      'kpi-cards': 12, 'my-tasks': 12, 'my-function-surveys': 12,
      'remediation-kpis': 12, 'remediation-attention': 12, 'remediation-priority': 5,
      'status-donut': 4, 'compliance-gauge': 3, 'completion-gauge': 4, 'quick-summary': 4,
    },
    // compliance-gauge ("Overall Compliance Rate") and standards-bar ("Compliance
    // by Standard") are both org-wide metrics — standards-bar isn't even scoped
    // by team/function on the backend, just tenant-wide — relevant to whoever
    // owns the framework, not to an individual contributor whose dashboard is
    // about their own task list. Hidden by default here for the same reason
    // `workload` (a team-wide chart) already was.
    defaultHidden: ['workload', 'compliance-gauge', 'standards-bar'],
    canCustomize: true,
    showComplianceFilter: false,
    showTeamView: false,
  },
};

/**
 * Detect the dashboard role from JWT claims.
 * Exact group name match first, then title keyword fallback for unknown groups.
 */
export function detectDashboardRole(groupName: string, title?: string): DashboardRole {
  // Normalize: lowercase, strip trailing underscore
  const g = (groupName ?? '').toLowerCase().trim().replace(/_$/, '');
  const t = (title ?? '').toLowerCase().trim();

  // ── Exact group matches (all known groups) ────────────────────────────────
  switch (g) {
    // Full platform admins
    case 'super_admin':
    case 'system_admin':
    case 'admin':
      return 'super_admin';

    // Compliance owners / framework managers
    case 'compliance_manager':
    case 'compliance_leader':
      return 'compliance_owner';

    // Compliance groups leader_ / manager_ / supervisor_ (trailing underscore stripped
    // above) — the layout follows the group, not the job title.
    case 'leader':
      return 'leader';

    // Business / department heads (see team view)
    case 'manager':
      return 'manager';

    case 'supervisor':
      return 'supervisor';

    // Elevated users — limited admin, cross-team visibility
    case 'user_admin':
    case 'business_compliance':
      return 'supervisor';

    // Regular users — personal tasks only
    case 'compliance_implementer':
    case 'business':
    case 'orm':
    case 'orm_testinggroups':
    case 'compliance_supervisor':
      return 'user';

    default:
      break;
  }

  // ── Title keyword fallback for unrecognised groups ────────────────────────
  const isCiso =
    t.includes('ciso') || t.includes('compliance officer') ||
    t.includes('compliance owner') || t.includes('compliance manager') ||
    t.includes('compliance leader') || t.includes('framework owner');
  if (isCiso) return 'compliance_owner';

  const isManager =
    t.includes('head of') || t.includes('manager') ||
    t.includes('director') || t.includes('vp ') || t.includes('vice president');
  if (isManager) return 'manager';

  const isSupervisor = t.includes('supervisor') || t.includes('lead');
  if (isSupervisor) return 'supervisor';

  return 'user';
}

/** The per-role localStorage key for widget layout. */
export function widgetStorageKey(role: DashboardRole): string {
  return `dashboard_widget_layout_v3_${role}`;
}
